import { useState } from 'react';
import { T } from 'gt-react';
import { DownloadSimple as DownloadSimpleIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

/**
 * Download button for a signed-storage-URL attachment. Signed URLs are
 * cross-origin and minted with inline disposition, so an `<a download>` is
 * ignored — instead the bytes are blob-fetched and saved through an object
 * URL (the SharedDocumentFullView pattern), falling back to opening the file
 * in a new tab when the fetch fails.
 */
export function DocumentDownloadButton({
  url,
  fileName,
  className,
}: {
  url: string;
  fileName: string;
  className?: string;
}) {
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download document (${response.status})`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      window.document.body.appendChild(anchor);
      anchor.click();
      window.document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(url, '_blank');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={className}
      disabled={downloading}
      onClick={() => void handleDownload()}
    >
      <DownloadSimpleIcon className="mr-1.5 size-4" />
      {downloading ? (
        <T context="download attachment button while in progress">
          Downloading…
        </T>
      ) : (
        <T context="download attachment button">Download</T>
      )}
    </Button>
  );
}
